/**
 * Card Component — Apex Design System v3.
 *
 * E1 elevation with inner light edge for premium depth perception.
 * The top edge catches the "ambient light" through an inset highlight,
 * making cards feel like physical objects on the surface.
 *
 * Variants:
 * - default: solid elevated surface
 * - glass: frosted glass with backdrop blur
 * - interactive: hover-lift with spring physics
 * - gradient: top gradient border accent (brand → accent)
 * - glow: hover glow effect with brand-tinted shadow
 */

import { clsx } from "clsx";
import type { HTMLAttributes } from "react";

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: "none" | "sm" | "md" | "lg";
  variant?: "default" | "glass" | "interactive" | "gradient" | "glow";
}

const paddingStyles = {
  none: "",
  sm: "p-4",
  md: "p-5",
  lg: "p-7",
};

function Card({
  padding = "md",
  variant = "default",
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={clsx(
        "rounded-[var(--radius-lg)] animate-fade-in",
        variant === "glass"
          ? "glass"
          : variant === "interactive"
            ? "elevation-1 hover-lift cursor-pointer"
            : variant === "gradient"
              ? "elevation-1 card-gradient"
              : variant === "glow"
                ? "elevation-1 hover-glow cursor-pointer"
                : "elevation-1",
        paddingStyles[padding],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export default Card;
